import { useState, useEffect } from 'react';
import './CrmSummary.css';
import { crmAPI } from '../../../config/crm-summary-api';

const CrmSummary = () => {
  const [summaryData, setSummaryData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [timeFilter, setTimeFilter] = useState('today');

  const fetchSummary = async (filter = 'today') => {
    try {
      setLoading(true);
      setError(null);

      const result = await crmAPI.getCrmSummary({ timeFilter: filter });

      if (result.success) {
        setSummaryData(result.data);
      } else {
        setError(result.msg || 'Failed to fetch summary');
      }
    } catch (err) {
      setError('Error fetching summary: ' + err.message);
      console.error('Error fetching summary:', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchSummary(timeFilter);
  }, []);

  const handleFilterChange = (filter) => {
    setTimeFilter(filter);
    fetchSummary(filter);
  };

  const signupFlowCards = [
    {
      key: 'welcomeMsgSent',
      label: 'Welcome Message Sent',
    },
    {
      key: 'rewardCouponsCreated',
      label: 'Reward Coupons Created',
    },
    {
      key: 'ZeroOrderUserCouponsCreated',
      label: 'Coupons Created for Zero Order Users',
    },
    {
      key: 'userConvertedWelcomeMsg',
      label: 'Users Converted After Welcome Message',
      percentageKey: 'userConvertedWelcomeMsgPercentage',
    },
    {
      key: 'userConvertedCoupon',
      label: 'Users Converted With Coupon',
      percentageKey: 'userConvertedCouponPercentage',
    },
    {
      key: 'userConvertedAfterCouponExtending',
      label: 'Users Converted After Coupon Extension',
      percentageKey: 'userConvertedAfterCouponExtendingPercentage',
    }
  ];

  const automatedPushNotificationCards = [
    {
      key: 'cartRedemptionMsg',
      label: 'Cart Redemption Message Sent',
    },
    {
      key: 'usersOpenedCartRedemption',
      label: 'Users Opened After Cart Redemption Message Sent',
      percentageKey: 'usersOpenedCartRedemptionPercentage',
    },
    {
      key: 'inactiveUsersMsg',
      label: 'Inactive Users Message Sent',
    },
    {
      key: 'usersOpenedInactiveUser',
      label: 'Users Opened After Inactive Users Message Sent',
      percentageKey: 'usersOpenedInactiveUserPercentage',
    },
    {
      key: 'feedbackMsgSent',
      label: 'Feedback Message Sent',
    }
  ];

  return (
    <div className="crm-summary-page">
      <div className="crm-page-header">
        <div className="crm-page-title">
          <h1>CRM Summary</h1>
          <p>Overview of crm summary</p>
        </div>
      </div>

      {loading ? (
        <div className="crm-loading-state">
          <div className="crm-loading-spinner"></div>
          <p>Loading summary...</p>
        </div>
      ) : error ? (
        <div className="crm-error-state">
          <p>{error}</p>
          <button onClick={() => fetchSummary(timeFilter)} className="crm-retry-btn">
            Retry
          </button>
        </div>
      ) : (
        <>
          <div className="crm-filter-wrapper">
            <div className="crm-filter-dropdown">
              <label>Time Period:</label>
              <select
                value={timeFilter}
                onChange={(e) => handleFilterChange(e.target.value)}
                className="crm-filter-select"
              >
                <option value="today">Today</option>
                <option value="yesterday">Yesterday</option>
                <option value="7days">Last 7 Days</option>
                <option value="30days">Last 30 Days</option>
              </select>
            </div>
          </div>

          <div className="crm-section-card">
            <div className="crm-section-header">
              <h2 className="crm-section-title">User Signup Flow</h2>
            </div>

            <div className="crm-cards-grid">
              {signupFlowCards.map((card) => {
                const value = summaryData?.user_signup_flow?.[card.key] || 0;
                const percentage = card.percentageKey
                  ? summaryData?.user_signup_flow?.[card.percentageKey]
                  : null;

                return (
                  <div
                    key={card.key}
                    className="crm-stat-card"
                    style={{ backgroundColor: card.color }}
                  >
                    <div className="crm-card-content">
                      <div className="crm-stat-label">
                        {card.label}
                      </div>
                      <div style={{ display: "flex", justifyContent: "center", gap: "5px" }}>
                        <div className="crm-stat-number">{value}</div>

                        {percentage !== null && percentage !== undefined && percentage > 0 && (
                          <div className="crm-stat-percentage">
                            ({percentage}%)
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>

          <div className="crm-section-card">
            <div className="crm-section-header">
              <h2 className="crm-section-title">Automated Push Notification</h2>
            </div>

            <div className="crm-cards-grid">
              {automatedPushNotificationCards.map((card) => {
                const value = summaryData?.automated_push_notification?.[card.key] || 0;
                const percentage = card.percentageKey
                  ? summaryData?.automated_push_notification?.[card.percentageKey]
                  : null;

                return (
                  <div
                    key={card.key}
                    className="crm-stat-card"
                    style={{ backgroundColor: card.color }}
                  >
                    <div className="crm-card-content">
                      <div className="crm-stat-label">
                        {card.label}
                      </div>
                      <div style={{ display: "flex", justifyContent: "center", gap: "5px" }}>
                        <div className="crm-stat-number">{value}</div>

                        {percentage !== null && percentage !== undefined && percentage > 0 && (
                          <div className="crm-stat-percentage">
                            ({percentage}%)
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default CrmSummary;